import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { AppHeader } from './AppHeader'
import { SideNav } from './SideNav'

/**
 * 应用外壳：顶栏 + 可选侧栏 + 内容区。
 *
 * 侧栏的出现规则来自设计稿：首页（D1）与详情页（D2）有左侧导航栏，搜索页（D3）
 * 左侧换成筛选面板，下载页与发布页是专注型页面、没有侧栏。所以侧栏由**路由**
 * 决定，而不是由 AppShell 一律渲染 —— 见 `SIDEBAR_ROUTES`。
 *
 * 侧栏在 `lg` 以下一律不渲染（移动端走抽屉，设计稿 M2），并且是 `sticky` 的：
 * 长列表滚动时导航跟着走，符合桌面端的使用习惯。
 */

/** 需要左侧导航栏的路由前缀。 */
const SIDEBAR_PREFIXES = ['/mods/', '/t/', '/ranking', '/favorites', '/creators/']

/**
 * 下载页是「专注型」页面（设计稿 D4 没有侧栏），但它的路径以 `/mods/` 开头，
 * 会被上面的前缀匹配到，所以要显式排除。
 */
const SIDEBAR_EXCLUDED = /^\/mods\/[^/]+\/download\/?$/

function shouldShowSidebar(pathname: string): boolean {
  if (SIDEBAR_EXCLUDED.test(pathname)) return false
  if (pathname === '/') return true
  return SIDEBAR_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

export function AppShell() {
  const { pathname } = useLocation()
  const [navCollapsed, setNavCollapsed] = useState(false)
  const showSidebar = shouldShowSidebar(pathname)

  // 路由切换后回到顶部。SPA 默认保留滚动位置，从长列表点进详情页会停在中间。
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [pathname])

  return (
    <div className="flex min-h-full flex-col bg-background">
      {/* 跳转到主内容：键盘用户不必逐个 Tab 过导航树。 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-card focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:text-primary-foreground"
      >
        跳到主要内容
      </a>

      <AppHeader />

      <div className="mx-auto flex w-full max-w-[100rem] flex-1 gap-4 px-3 py-4 sm:px-4 sm:py-5 lg:gap-5 lg:px-6">
        {showSidebar && (
          <aside
            className={`hidden shrink-0 lg:block ${navCollapsed ? 'w-14' : 'w-56 xl:w-64'}`}
            aria-label="侧边导航"
          >
            <div className="sticky top-20">
              <SideNav
                collapsed={navCollapsed}
                onToggleCollapse={() => setNavCollapsed((value) => !value)}
              />
            </div>
          </aside>
        )}

        {/* min-w-0 是必需的：没有它，内部的 grid/truncate 会把 flex 子项撑破。 */}
        <main id="main-content" className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>

      <SiteFooter />
    </div>
  )
}

function SiteFooter() {
  return (
    <footer className="mt-6 border-t border-border py-6">
      <div className="mx-auto max-w-[100rem] space-y-2 px-4 text-center text-xs leading-6 text-subtle-foreground lg:px-6">
        <p>
          APKGal（apkgal.com）是一个分享移植到 Android 的 galgame
          的平台，收录视觉小说安卓移植版、各引擎移植包、汉化补丁及安装教程，帮助玩家在手机上畅玩
          galgame
        </p>
        {/*
          这行不是免责套话，是后端契约的明文要求：
          openapi.yaml 声明「不做任何病毒/恶意软件/压缩包内容扫描」，
          且**不得声称资源经过安全扫描**。前端也不能暗示相反的事。
        */}
        <p>站内资源由用户上传，未经安全扫描，请自行判断风险后使用</p>
      </div>
    </footer>
  )
}
